import React, { useState } from 'react';
import { todayIso, toIsoDate } from '../utils/dateUtils';
import { exportToPdf } from '../utils/exportToPdf';
import { exportSectionsToCsv, exportSectionsToExcel } from '../utils/exportData';

function PmsGmReports({ token, selectedPropertyId, selectedProperty }) {
  const [activeTab, setActiveTab] = useState('daily-flash');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const today = todayIso();
  const mondayOffset = new Date().getDay() === 0 ? 6 : new Date().getDay() - 1;
  const mondayDate = new Date(); mondayDate.setDate(mondayDate.getDate() - mondayOffset);
  const defaultWeekStart = toIsoDate(mondayDate);
  const defaultMonth = today.slice(0, 7);

  const [flashDate, setFlashDate] = useState(today);
  const [weekStart, setWeekStart] = useState(defaultWeekStart);
  const [month, setMonth] = useState(defaultMonth);

  const [flashData, setFlashData] = useState(null);
  const [weeklyData, setWeeklyData] = useState(null);
  const [monthlyData, setMonthlyData] = useState(null);

  const resolvePropertyId = () => String(selectedPropertyId || selectedProperty?.id || localStorage.getItem('hotelConfigSelectedPropertyId') || '').trim();
  const resolveChainId = () => String(selectedProperty?.chain_id || localStorage.getItem('hotelConfigSelectedChainId') || '').trim();
  const authHeaders = () => ({
    Authorization: `Bearer ${token}`,
    'x-property-id': resolvePropertyId(),
    ...(resolveChainId() ? { 'x-chain-id': resolveChainId() } : {}),
  });

  const fmt = (v) => Number(v || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const fmtInt = (v) => Number(v || 0).toLocaleString('en-IN');
  const pct = (v) => Number(v || 0).toFixed(1) + '%';

  // ── Fetch Daily Flash ──
  const fetchFlash = async () => {
    if (!resolvePropertyId()) return setError('Select a property first');
    setLoading(true); setError(''); setFlashData(null);
    try {
      const res = await fetch(`/api/v1/gm-reports/daily-flash?date=${flashDate}`, { headers: authHeaders() });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || 'Failed to load Daily Flash');
      setFlashData(data);
    } catch (err) { setError(err.message); } finally { setLoading(false); }
  };

  // ── Fetch Weekly ──
  const fetchWeekly = async () => {
    if (!resolvePropertyId()) return setError('Select a property first');
    setLoading(true); setError(''); setWeeklyData(null);
    try {
      const res = await fetch(`/api/v1/gm-reports/weekly?week_start=${weekStart}`, { headers: authHeaders() });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || 'Failed to load Weekly Report');
      setWeeklyData(data);
    } catch (err) { setError(err.message); } finally { setLoading(false); }
  };

  // ── Fetch Monthly ──
  const fetchMonthly = async () => {
    if (!resolvePropertyId()) return setError('Select a property first');
    setLoading(true); setError(''); setMonthlyData(null);
    try {
      const res = await fetch(`/api/v1/gm-reports/monthly?month=${month}`, { headers: authHeaders() });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || 'Failed to load Monthly Report');
      setMonthlyData(data);
    } catch (err) { setError(err.message); } finally { setLoading(false); }
  };

  // ── CSV Export ──
  const downloadCsv = (filename, headerRow, dataRows) => {
    const csvContent = [headerRow.join(','), ...dataRows.map(r => r.map(c => `"${String(c ?? '').replace(/"/g, '""')}"`).join(','))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    link.click();
  };

  // ── PDF Exports (replaces window.print() snapshots — exports the underlying data) ──
  const propertyName = selectedProperty?.name || '';
  const downloadFlash = (fmt = 'pdf') => {
    if (!flashData) return;
    const d = flashData;
    const rs = d.room_statistics; const rv = d.revenue_summary;
    const k = d.kpis; const gm = d.guest_movement; const rp = d.reservation_pickup;
    const sections = [
      {
        heading: 'KPI Snapshot',
        columns: [{ header: 'Metric', dataKey: 'm' }, { header: 'Value', dataKey: 'v', halign: 'right' }],
        rows: [
          { m: 'Occupancy', v: pct(k.occupancy_percent) },
          { m: 'ADR', v: fmt(k.adr) },
          { m: 'RevPAR', v: fmt(k.revpar) },
          { m: 'TRevPAR', v: fmt(k.trevpar) },
          { m: 'Total Revenue', v: fmt(rv.total_gross_revenue) },
          { m: 'Total Tax', v: fmt(rv.total_tax) },
          { m: 'Total Payments', v: fmt(rv.total_payments) },
        ],
      },
      {
        heading: 'Room Statistics',
        columns: [
          { header: 'Room Type', dataKey: 'room_type_name' },
          { header: 'Total', dataKey: 'total_rooms', halign: 'right' },
          { header: 'OOO', dataKey: 'ooo', halign: 'right' },
          { header: 'Sellable', dataKey: 'sellable', halign: 'right' },
          { header: 'Occupied', dataKey: 'occupied', halign: 'right' },
          { header: 'Available', dataKey: 'available', halign: 'right' },
          { header: 'Stayovers', dataKey: 'stayovers', halign: 'right' },
          { header: 'Arrivals', dataKey: 'arrivals', halign: 'right' },
          { header: 'Departures', dataKey: 'departures', halign: 'right' },
        ],
        rows: rs.by_room_type || [],
      },
      {
        heading: 'Revenue Summary',
        columns: [
          { header: 'Category', dataKey: 'category' },
          { header: 'Amount', dataKey: '_amount', halign: 'right' },
          { header: 'CGST', dataKey: '_cgst', halign: 'right' },
          { header: 'SGST', dataKey: '_sgst', halign: 'right' },
          { header: 'IGST', dataKey: '_igst', halign: 'right' },
        ],
        rows: [
          ...(rv.lines || []).map(r => ({ category: r.code, _amount: fmt(r.amount), _cgst: fmt(r.cgst), _sgst: fmt(r.sgst), _igst: fmt(r.igst) })),
          ...(rv.fnb_by_outlet || []).map(r => ({ category: `F&B Outlet #${r.outlet_id}`, _amount: fmt(r.revenue), _cgst: fmt(r.cgst), _sgst: fmt(r.sgst), _igst: '0.00' })),
          { category: 'GROSS TOTAL', _amount: fmt(rv.total_gross_revenue), _cgst: fmt(rv.cgst), _sgst: fmt(rv.sgst), _igst: fmt(rv.igst) },
        ],
      },
      {
        heading: 'Guest Movement',
        columns: [{ header: 'Metric', dataKey: 'm' }, { header: 'Count', dataKey: 'v', halign: 'right' }],
        rows: [
          { m: 'Arrivals', v: fmtInt(gm.arrivals) },
          { m: 'Departures', v: fmtInt(gm.departures) },
          { m: 'Stayovers', v: fmtInt(gm.stayovers) },
          { m: 'Walk-ins', v: fmtInt(gm.walk_ins) },
          { m: 'In-house Pax', v: fmtInt(gm.in_house_pax) },
          { m: 'No-shows', v: fmtInt(gm.no_shows) },
          { m: 'New Bookings', v: fmtInt(rp.new_bookings) },
          { m: 'Cancellations', v: fmtInt(rp.cancellations) },
          { m: 'Net Pickup', v: fmtInt(rp.net_pickup) },
        ],
      },
    ];
    if (Array.isArray(d.segment_snapshot) && d.segment_snapshot.length) {
      sections.push({
        heading: 'Segment Snapshot (In-house)',
        columns: [
          { header: 'Segment', dataKey: 'segment' },
          { header: 'Rooms', dataKey: 'rooms', halign: 'right' },
          { header: 'Revenue', dataKey: '_revenue', halign: 'right' },
        ],
        rows: d.segment_snapshot.map(r => ({ ...r, _revenue: fmt(r.revenue) })),
      });
    }
    const base = `daily_flash_${flashDate}`;
    if (fmt === 'csv') { exportSectionsToCsv(base, sections); return; }
    if (fmt === 'excel') { exportSectionsToExcel(base, sections, 'Daily Flash Report'); return; }
    exportToPdf({
      title: 'Daily Flash Report',
      subtitle: propertyName ? `${propertyName} — ${flashDate}` : flashDate,
      meta: [`Generated: ${new Date().toLocaleString()}`],
      sections,
      orientation: 'landscape',
      filename: `${base}.pdf`,
      footerLeft: propertyName,
    });
  };

  const downloadWeeklyPdf = () => {
    if (!weeklyData) return;
    const d = weeklyData; const s = d.weekly_summary;
    const sections = [
      {
        heading: 'Weekly Summary',
        columns: [{ header: 'Metric', dataKey: 'm' }, { header: 'Value', dataKey: 'v', halign: 'right' }],
        rows: [
          { m: 'Total Room Nights', v: fmtInt(s.total_room_nights) },
          { m: 'Days with Data', v: fmtInt(s.days_with_data) },
          { m: 'Avg Occupancy', v: pct(s.avg_occupancy) },
          { m: 'Avg ADR', v: fmt(s.avg_adr) },
          { m: 'Avg RevPAR', v: fmt(s.avg_revpar) },
          { m: 'Total Revenue', v: fmt(s.total_revenue) },
          { m: 'Total Tax', v: fmt(s.total_tax) },
          { m: 'Total Payments', v: fmt(s.total_payments) },
        ],
      },
      {
        heading: 'Daily Performance Grid',
        columns: [
          { header: 'Date', dataKey: 'date' },
          { header: 'Occupied', dataKey: 'rooms_occupied', halign: 'right' },
          { header: 'Occ%', dataKey: '_occ', halign: 'right' },
          { header: 'ADR', dataKey: '_adr', halign: 'right' },
          { header: 'RevPAR', dataKey: '_revpar', halign: 'right' },
          { header: 'Room Rev', dataKey: '_room_rev', halign: 'right' },
          { header: 'F&B Rev', dataKey: '_fnb_rev', halign: 'right' },
          { header: 'Total Rev', dataKey: '_total_rev', halign: 'right' },
          { header: 'Arrivals', dataKey: 'arrivals', halign: 'right' },
          { header: 'Departures', dataKey: 'departures', halign: 'right' },
        ],
        rows: (d.daily_grid || []).map(r => ({
          ...r,
          _occ: pct(r.occupancy_percent), _adr: fmt(r.adr), _revpar: fmt(r.revpar),
          _room_rev: fmt(r.room_revenue), _fnb_rev: fmt(r.fnb_revenue), _total_rev: fmt(r.total_revenue),
        })),
      },
    ];
    if (Array.isArray(d.segment_performance) && d.segment_performance.length) {
      sections.push({
        heading: 'Segment Performance',
        columns: [
          { header: 'Segment', dataKey: 'segment' },
          { header: 'Reservations', dataKey: 'reservations', halign: 'right' },
          { header: 'Room Nights', dataKey: 'room_nights', halign: 'right' },
          { header: 'Revenue', dataKey: '_revenue', halign: 'right' },
        ],
        rows: d.segment_performance.map(r => ({ ...r, _revenue: fmt(r.revenue) })),
      });
    }
    if (Array.isArray(d.channel_performance) && d.channel_performance.length) {
      sections.push({
        heading: 'Channel Performance',
        columns: [
          { header: 'Channel', dataKey: 'channel' },
          { header: 'Bookings', dataKey: 'bookings', halign: 'right' },
          { header: 'Room Nights', dataKey: 'room_nights', halign: 'right' },
          { header: 'Revenue', dataKey: '_revenue', halign: 'right' },
        ],
        rows: d.channel_performance.map(r => ({ ...r, _revenue: fmt(r.revenue) })),
      });
    }
    if (Array.isArray(d.room_type_performance) && d.room_type_performance.length) {
      sections.push({
        heading: 'Room Type Performance',
        columns: [
          { header: 'Room Type', dataKey: 'room_type_name' },
          { header: 'Inventory', dataKey: 'total_inventory_nights', halign: 'right' },
          { header: 'Sellable', dataKey: 'sellable_nights', halign: 'right' },
          { header: 'Sold', dataKey: 'sold_nights', halign: 'right' },
          { header: 'Occupancy%', dataKey: '_occ', halign: 'right' },
        ],
        rows: d.room_type_performance.map(r => ({ ...r, _occ: pct(r.occupancy_percent) })),
      });
    }
    exportToPdf({
      title: 'Weekly Report',
      subtitle: propertyName ? `${propertyName} — Week starting ${weekStart}` : `Week starting ${weekStart}`,
      meta: [`Generated: ${new Date().toLocaleString()}`],
      sections,
      orientation: 'landscape',
      filename: `weekly_report_${weekStart}.pdf`,
      footerLeft: propertyName,
    });
  };

  const downloadMonthlyPdf = () => {
    if (!monthlyData) return;
    const d = monthlyData; const f = d.financial_summary; const ts = d.tax_summary;
    const sections = [
      {
        heading: 'Financial Summary',
        columns: [{ header: 'Metric', dataKey: 'm' }, { header: 'Value', dataKey: 'v', halign: 'right' }],
        rows: [
          { m: 'Total Room Nights', v: fmtInt(f.total_room_nights) },
          { m: 'Days with Data', v: fmtInt(f.days_with_data) },
          { m: 'Avg Occupancy', v: pct(f.avg_occupancy) },
          { m: 'Avg ADR', v: fmt(f.avg_adr) },
          { m: 'Avg RevPAR', v: fmt(f.avg_revpar) },
          { m: 'Total Room Revenue', v: fmt(f.total_room_revenue) },
          { m: 'Total F&B Revenue', v: fmt(f.total_fnb_revenue) },
          { m: 'Total Revenue', v: fmt(f.total_revenue) },
          { m: 'Total Tax', v: fmt(f.total_tax) },
        ],
      },
      {
        heading: 'Revenue by Department',
        columns: [
          { header: 'Department', dataKey: 'department' },
          { header: 'Gross', dataKey: '_gross', halign: 'right' },
          { header: 'CGST', dataKey: '_cgst', halign: 'right' },
          { header: 'SGST', dataKey: '_sgst', halign: 'right' },
          { header: 'IGST', dataKey: '_igst', halign: 'right' },
          { header: 'Total', dataKey: '_total', halign: 'right' },
        ],
        rows: (d.revenue_by_department || []).map(r => ({
          department: r.department,
          _gross: fmt(r.gross_amount), _cgst: fmt(r.cgst), _sgst: fmt(r.sgst), _igst: fmt(r.igst),
          _total: fmt(Number(r.gross_amount || 0) + Number(r.cgst || 0) + Number(r.sgst || 0) + Number(r.igst || 0)),
        })),
      },
      {
        heading: 'Revenue by Segment',
        columns: [
          { header: 'Segment', dataKey: 'segment' },
          { header: 'Reservations', dataKey: 'reservations', halign: 'right' },
          { header: 'Room Nights', dataKey: 'room_nights', halign: 'right' },
          { header: 'Revenue', dataKey: '_revenue', halign: 'right' },
        ],
        rows: (d.revenue_by_segment || []).map(r => ({ ...r, _revenue: fmt(r.revenue) })),
      },
      {
        heading: 'Tax Summary',
        columns: [
          { header: 'Tax', dataKey: 'tax' },
          { header: 'Folio', dataKey: '_folio', halign: 'right' },
          { header: 'F&B', dataKey: '_fnb', halign: 'right' },
          { header: 'Total', dataKey: '_total', halign: 'right' },
        ],
        rows: [
          { tax: 'CGST', _folio: fmt(ts.folio_cgst), _fnb: fmt(ts.fnb_cgst), _total: fmt(ts.total_cgst) },
          { tax: 'SGST', _folio: fmt(ts.folio_sgst), _fnb: fmt(ts.fnb_sgst), _total: fmt(ts.total_sgst) },
          { tax: 'IGST', _folio: fmt(ts.folio_igst), _fnb: '0.00', _total: fmt(ts.total_igst) },
          { tax: 'TOTAL', _folio: '', _fnb: '', _total: fmt(ts.grand_total) },
        ],
      },
    ];
    // Bug 8688 — Outstanding Summary was shown on screen and in CSV but missing
    // from the PDF. Add it (open folios + corporate + travel-agent outstanding).
    {
      const os = d.outstanding_summary || {};
      const osRows = [
        { party: 'Open Folios', detail: `${os.open_folios?.count || 0} folio(s)`, _used: fmt(os.open_folios?.total_balance), _limit: '' },
      ];
      (os.corporate || []).forEach(r => osRows.push({ party: 'Corporate', detail: r.name, _used: fmt(r.credit_used), _limit: fmt(r.credit_limit) }));
      (os.travel_agents || []).forEach(r => osRows.push({ party: 'Travel Agent', detail: r.name, _used: fmt(r.credit_used), _limit: fmt(r.credit_limit) }));
      sections.push({
        heading: 'Outstanding Summary',
        columns: [
          { header: 'Party', dataKey: 'party' },
          { header: 'Detail', dataKey: 'detail' },
          { header: 'Used / Balance', dataKey: '_used', halign: 'right' },
          { header: 'Credit Limit', dataKey: '_limit', halign: 'right' },
        ],
        rows: osRows,
      });
    }
    if (Array.isArray(d.settlement_analysis) && d.settlement_analysis.length) {
      sections.push({
        heading: 'Settlement Analysis',
        columns: [
          { header: 'Mode', dataKey: 'mode' },
          { header: 'Invoices', dataKey: 'invoice_count', halign: 'right' },
          { header: 'Billed', dataKey: '_billed', halign: 'right' },
          { header: 'Collected', dataKey: '_collected', halign: 'right' },
          { header: 'Outstanding', dataKey: '_outstanding', halign: 'right' },
        ],
        rows: d.settlement_analysis.map(r => ({
          ...r,
          _billed: fmt(r.total_billed), _collected: fmt(r.total_collected), _outstanding: fmt(r.outstanding),
        })),
      });
    }
    if (Array.isArray(d.daily_trend) && d.daily_trend.length) {
      sections.push({
        heading: 'Daily Trend',
        columns: [
          { header: 'Date', dataKey: 'date' },
          { header: 'Occupied', dataKey: 'rooms_occupied', halign: 'right' },
          { header: 'Occ%', dataKey: '_occ', halign: 'right' },
          { header: 'ADR', dataKey: '_adr', halign: 'right' },
          { header: 'Room Rev', dataKey: '_room_rev', halign: 'right' },
          { header: 'F&B Rev', dataKey: '_fnb_rev', halign: 'right' },
          { header: 'Total Rev', dataKey: '_total_rev', halign: 'right' },
          { header: 'Arrivals', dataKey: 'arrivals', halign: 'right' },
          { header: 'Departures', dataKey: 'departures', halign: 'right' },
        ],
        rows: d.daily_trend.map(r => ({
          ...r,
          _occ: pct(r.occupancy_percent), _adr: fmt(r.adr),
          _room_rev: fmt(r.room_revenue), _fnb_rev: fmt(r.fnb_revenue), _total_rev: fmt(r.total_revenue),
        })),
      });
    }
    exportToPdf({
      title: 'Monthly Report',
      subtitle: propertyName ? `${propertyName} — ${month}` : month,
      meta: [`Generated: ${new Date().toLocaleString()}`],
      sections,
      orientation: 'landscape',
      filename: `monthly_report_${month}.pdf`,
      footerLeft: propertyName,
    });
  };

  // ── Shared Helpers ──
  const kpiCard = (label, value, sub) => (
    <div key={label} className="gm-kpi-card">
      <div className="gm-kpi-label">{label}</div>
      <div className="gm-kpi-value">{value}</div>
      {sub && <div className="gm-kpi-sub">{sub}</div>}
    </div>
  );

  // ═══════════════════════════════════════════════════════════════════════
  // DAILY FLASH RENDER
  // ═══════════════════════════════════════════════════════════════════════
  const renderFlash = () => {
    if (!flashData) return null;
    const d = flashData;
    const rs = d.room_statistics;
    const rv = d.revenue_summary;
    const k = d.kpis;
    const gm = d.guest_movement;
    const rp = d.reservation_pickup;
    const alerts = d.operational_alerts;

    return (
      <div>
        {/* KPI Cards */}
        <div className="gm-kpi-row">
          {kpiCard('Occupancy', pct(k.occupancy_percent), `${rs.occupied} / ${rs.sellable} sellable`)}
          {kpiCard('ADR', `₹${fmt(k.adr)}`, 'Avg Daily Rate')}
          {kpiCard('RevPAR', `₹${fmt(k.revpar)}`, 'Rev per Available Room')}
          {kpiCard('TRevPAR', `₹${fmt(k.trevpar)}`, 'Total Rev per Available')}
          {kpiCard('Total Revenue', `₹${fmt(rv.total_gross_revenue)}`, `Tax: ₹${fmt(rv.total_tax)}`)}
          {kpiCard('Payments', `₹${fmt(rv.total_payments)}`, 'Collected Today')}
        </div>

        {/* Room Statistics */}
        <h4 className="gm-section-title">Room Statistics</h4>
        <div className="gm-scroll-x">
          <table className="gm-table">
            <thead><tr>
              <th>Room Type</th><th className="text-right">Total</th><th className="text-right">OOO</th><th className="text-right">Sellable</th>
              <th className="text-right">Occupied</th><th className="text-right">Available</th><th className="text-right">Stayovers</th><th className="text-right">Arrivals</th><th className="text-right">Departures</th>
            </tr></thead>
            <tbody>
              {(rs.by_room_type || []).map((r, i) => (
                <tr key={i}>
                  <td>{r.room_type_name}</td><td className="text-right">{r.total_rooms}</td><td className="text-right">{r.ooo}</td><td className="text-right">{r.sellable}</td>
                  <td className="text-right">{r.occupied}</td><td className="text-right">{r.available}</td><td className="text-right">{r.stayovers}</td><td className="text-right">{r.arrivals}</td><td className="text-right">{r.departures}</td>
                </tr>
              ))}
              <tr className="total-row">
                <td>TOTAL</td><td className="text-right">{rs.total_rooms}</td><td className="text-right">{rs.out_of_order}</td><td className="text-right">{rs.sellable}</td>
                <td className="text-right">{rs.occupied}</td><td className="text-right">{rs.available}</td><td className="text-right">{gm.stayovers}</td><td className="text-right">{gm.arrivals}</td><td className="text-right">{gm.departures}</td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Revenue Summary */}
        <h4 className="gm-section-title">Revenue Summary</h4>
        <div className="gm-scroll-x">
          <table className="gm-table">
            <thead><tr>
              <th>Category</th><th className="text-right">Amount</th><th className="text-right">CGST</th><th className="text-right">SGST</th><th className="text-right">IGST</th>
            </tr></thead>
            <tbody>
              {(rv.lines || []).map((r, i) => (
                <tr key={i}><td>{r.code}</td><td className="text-right">{fmt(r.amount)}</td><td className="text-right">{fmt(r.cgst)}</td><td className="text-right">{fmt(r.sgst)}</td><td className="text-right">{fmt(r.igst)}</td></tr>
              ))}
              {(rv.fnb_by_outlet || []).map((r, i) => (
                <tr key={'fnb' + i}><td>F&B Outlet #{r.outlet_id}</td><td className="text-right">{fmt(r.revenue)}</td><td className="text-right">{fmt(r.cgst)}</td><td className="text-right">{fmt(r.sgst)}</td><td className="text-right">0.00</td></tr>
              ))}
              <tr className="total-row">
                <td>GROSS TOTAL</td><td className="text-right">{fmt(rv.total_gross_revenue)}</td>
                <td className="text-right">{fmt(rv.cgst)}</td><td className="text-right">{fmt(rv.sgst)}</td><td className="text-right">{fmt(rv.igst)}</td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Guest Movement + Reservation Pickup */}
        <h4 className="gm-section-title">Guest Movement & Reservation Pickup</h4>
        <div className="gm-flex-pair">
          <div className="gm-flex-half">
            <table className="gm-table">
              <tbody>
                {[['Arrivals', gm.arrivals], ['Departures', gm.departures], ['Stayovers', gm.stayovers], ['Walk-ins', gm.walk_ins], ['In-house Pax', gm.in_house_pax], ['No-shows', gm.no_shows]].map(([l, v]) => (
                  <tr key={l}><td>{l}</td><td className="text-right">{fmtInt(v)}</td></tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="gm-flex-half">
            <table className="gm-table">
              <tbody>
                <tr><td>New Bookings Today</td><td className="text-right">{fmtInt(rp.new_bookings)}</td></tr>
                <tr><td>Cancellations Today</td><td className="text-right">{fmtInt(rp.cancellations)}</td></tr>
                <tr className="total-row"><td>Net Pickup</td><td className="text-right">{fmtInt(rp.net_pickup)}</td></tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* Segment Snapshot */}
        <h4 className="gm-section-title">Segment Snapshot (In-house)</h4>
        <table className="gm-table">
          <thead><tr><th>Segment</th><th className="text-right">Rooms</th><th className="text-right">Revenue</th></tr></thead>
          <tbody>
            {(d.segment_snapshot || []).map((r, i) => (
              <tr key={i}><td>{r.segment}</td><td className="text-right">{r.rooms}</td><td className="text-right">{fmt(r.revenue)}</td></tr>
            ))}
          </tbody>
        </table>

        {/* Operational Alerts */}
        <h4 className="gm-section-title">Operational Alerts</h4>
        <div className="gm-flex-pair">
          <div className="gm-alert-card danger">
            <strong>OOO Rooms ({alerts.ooo_rooms?.length || 0})</strong>
            <div className="alert-detail">{(alerts.ooo_rooms || []).map(r => r.room_number).join(', ') || 'None'}</div>
          </div>
          <div className="gm-alert-card warning">
            <strong>Unsettled Folios</strong>
            <div className="alert-detail">{alerts.unsettled_folios?.count || 0} folios, ₹{fmt(alerts.unsettled_folios?.total_balance)}</div>
          </div>
          <div className="gm-alert-card danger">
            <strong>Late Checkouts ({alerts.late_checkouts?.length || 0})</strong>
            <div className="alert-detail">{(alerts.late_checkouts || []).map(r => `${r.room_number} (${r.guest_name})`).join(', ') || 'None'}</div>
          </div>
        </div>
        {(alerts.top_corporate_outstanding?.length > 0 || alerts.top_ta_outstanding?.length > 0) && (
          <div className="gm-flex-pair" style={{ marginTop: '0.5rem' }}>
            {alerts.top_corporate_outstanding?.length > 0 && (
              <div className="gm-flex-half">
                <strong style={{ fontSize: '0.8rem' }}>Top Corporate Outstanding</strong>
                <table className="gm-table" style={{ marginTop: '4px' }}>
                  <thead><tr><th>Corporate</th><th className="text-right">Used</th><th className="text-right">Limit</th></tr></thead>
                  <tbody>{alerts.top_corporate_outstanding.map((r, i) => (
                    <tr key={i}><td>{r.name}</td><td className="text-right">{fmt(r.credit_used)}</td><td className="text-right">{fmt(r.credit_limit)}</td></tr>
                  ))}</tbody>
                </table>
              </div>
            )}
            {alerts.top_ta_outstanding?.length > 0 && (
              <div className="gm-flex-half">
                <strong style={{ fontSize: '0.8rem' }}>Top TA Outstanding</strong>
                <table className="gm-table" style={{ marginTop: '4px' }}>
                  <thead><tr><th>Travel Agent</th><th className="text-right">Used</th><th className="text-right">Limit</th></tr></thead>
                  <tbody>{alerts.top_ta_outstanding.map((r, i) => (
                    <tr key={i}><td>{r.name}</td><td className="text-right">{fmt(r.credit_used)}</td><td className="text-right">{fmt(r.credit_limit)}</td></tr>
                  ))}</tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  // ═══════════════════════════════════════════════════════════════════════
  // WEEKLY RENDER
  // ═══════════════════════════════════════════════════════════════════════
  const renderWeekly = () => {
    if (!weeklyData) return null;
    const d = weeklyData;
    const s = d.weekly_summary;

    return (
      <div>
        {/* Summary Cards */}
        <div className="gm-kpi-row">
          {kpiCard('Room Nights', fmtInt(s.total_room_nights), `${s.days_with_data} days data`)}
          {kpiCard('Avg Occupancy', pct(s.avg_occupancy))}
          {kpiCard('Avg ADR', `₹${fmt(s.avg_adr)}`)}
          {kpiCard('Avg RevPAR', `₹${fmt(s.avg_revpar)}`)}
          {kpiCard('Total Revenue', `₹${fmt(s.total_revenue)}`, `Tax: ₹${fmt(s.total_tax)}`)}
          {kpiCard('Payments', `₹${fmt(s.total_payments)}`)}
        </div>

        {/* Daily Grid */}
        <h4 className="gm-section-title">Daily Performance Grid</h4>
        <div className="gm-scroll-x">
          <table className="gm-table">
            <thead><tr>
              <th>Date</th><th className="text-right">Occupied</th><th className="text-right">Occ%</th><th className="text-right">ADR</th><th className="text-right">RevPAR</th>
              <th className="text-right">Room Rev</th><th className="text-right">F&B Rev</th><th className="text-right">Total Rev</th><th className="text-right">Arrivals</th><th className="text-right">Departures</th>
            </tr></thead>
            <tbody>
              {(d.daily_grid || []).map((r, i) => (
                <tr key={i}>
                  <td>{r.date}</td><td className="text-right">{r.rooms_occupied}</td><td className="text-right">{pct(r.occupancy_percent)}</td>
                  <td className="text-right">{fmt(r.adr)}</td><td className="text-right">{fmt(r.revpar)}</td>
                  <td className="text-right">{fmt(r.room_revenue)}</td><td className="text-right">{fmt(r.fnb_revenue)}</td><td className="text-right">{fmt(r.total_revenue)}</td>
                  <td className="text-right">{r.arrivals}</td><td className="text-right">{r.departures}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Segment + Channel + Room Type */}
        <div className="gm-flex-pair" style={{ marginTop: '0.5rem' }}>
          <div className="gm-flex-half">
            <h4 className="gm-section-title">Segment Performance</h4>
            <table className="gm-table">
              <thead><tr><th>Segment</th><th className="text-right">Reservations</th><th className="text-right">Room Nights</th><th className="text-right">Revenue</th></tr></thead>
              <tbody>{(d.segment_performance || []).map((r, i) => (
                <tr key={i}><td>{r.segment}</td><td className="text-right">{r.reservations}</td><td className="text-right">{r.room_nights}</td><td className="text-right">{fmt(r.revenue)}</td></tr>
              ))}</tbody>
            </table>
          </div>
          <div className="gm-flex-half">
            <h4 className="gm-section-title">Channel Performance</h4>
            <table className="gm-table">
              <thead><tr><th>Channel</th><th className="text-right">Bookings</th><th className="text-right">Room Nights</th><th className="text-right">Revenue</th></tr></thead>
              <tbody>{(d.channel_performance || []).map((r, i) => (
                <tr key={i}><td>{r.channel}</td><td className="text-right">{r.bookings}</td><td className="text-right">{r.room_nights}</td><td className="text-right">{fmt(r.revenue)}</td></tr>
              ))}</tbody>
            </table>
          </div>
        </div>

        <h4 className="gm-section-title">Room Type Performance</h4>
        <table className="gm-table">
          <thead><tr><th>Room Type</th><th className="text-right">Inventory</th><th className="text-right">Sellable</th><th className="text-right">Sold</th><th className="text-right">Occupancy%</th></tr></thead>
          <tbody>{(d.room_type_performance || []).map((r, i) => (
            <tr key={i}><td>{r.room_type_name}</td><td className="text-right">{r.total_inventory_nights}</td><td className="text-right">{r.sellable_nights}</td><td className="text-right">{r.sold_nights}</td><td className="text-right">{pct(r.occupancy_percent)}</td></tr>
          ))}</tbody>
        </table>
      </div>
    );
  };

  // ═══════════════════════════════════════════════════════════════════════
  // MONTHLY RENDER
  // ═══════════════════════════════════════════════════════════════════════
  const renderMonthly = () => {
    if (!monthlyData) return null;
    const d = monthlyData;
    const f = d.financial_summary;
    const ts = d.tax_summary;

    return (
      <div>
        {/* Financial Summary Cards */}
        <div className="gm-kpi-row">
          {kpiCard('Room Nights', fmtInt(f.total_room_nights), `${f.days_with_data} days data`)}
          {kpiCard('Avg Occupancy', pct(f.avg_occupancy))}
          {kpiCard('Avg ADR', `₹${fmt(f.avg_adr)}`)}
          {kpiCard('Avg RevPAR', `₹${fmt(f.avg_revpar)}`)}
          {kpiCard('Total Revenue', `₹${fmt(f.total_revenue)}`, `Room: ₹${fmt(f.total_room_revenue)} | F&B: ₹${fmt(f.total_fnb_revenue)}`)}
          {kpiCard('Total Tax', `₹${fmt(f.total_tax)}`)}
        </div>

        {/* Revenue by Department */}
        <h4 className="gm-section-title">Revenue by Department</h4>
        <table className="gm-table">
          <thead><tr><th>Department</th><th className="text-right">Gross Amount</th><th className="text-right">CGST</th><th className="text-right">SGST</th><th className="text-right">IGST</th><th className="text-right">Total</th></tr></thead>
          <tbody>{(d.revenue_by_department || []).map((r, i) => (
            <tr key={i}>
              <td>{r.department}</td><td className="text-right">{fmt(r.gross_amount)}</td><td className="text-right">{fmt(r.cgst)}</td><td className="text-right">{fmt(r.sgst)}</td><td className="text-right">{fmt(r.igst)}</td>
              <td className="text-right">{fmt(Number(r.gross_amount || 0) + Number(r.cgst || 0) + Number(r.sgst || 0) + Number(r.igst || 0))}</td>
            </tr>
          ))}</tbody>
        </table>

        {/* Revenue by Segment */}
        <h4 className="gm-section-title">Revenue by Segment</h4>
        <table className="gm-table">
          <thead><tr><th>Segment</th><th className="text-right">Reservations</th><th className="text-right">Room Nights</th><th className="text-right">Revenue</th></tr></thead>
          <tbody>{(d.revenue_by_segment || []).map((r, i) => (
            <tr key={i}><td>{r.segment}</td><td className="text-right">{r.reservations}</td><td className="text-right">{r.room_nights}</td><td className="text-right">{fmt(r.revenue)}</td></tr>
          ))}</tbody>
        </table>

        {/* Tax Summary */}
        <h4 className="gm-section-title">Tax Summary</h4>
        <table className="gm-table" style={{ maxWidth: '500px' }}>
          <thead><tr><th>Tax Type</th><th className="text-right">Folio</th><th className="text-right">F&B</th><th className="text-right">Total</th></tr></thead>
          <tbody>
            <tr><td>CGST</td><td className="text-right">{fmt(ts.folio_cgst)}</td><td className="text-right">{fmt(ts.fnb_cgst)}</td><td className="text-right">{fmt(ts.total_cgst)}</td></tr>
            <tr><td>SGST</td><td className="text-right">{fmt(ts.folio_sgst)}</td><td className="text-right">{fmt(ts.fnb_sgst)}</td><td className="text-right">{fmt(ts.total_sgst)}</td></tr>
            <tr><td>IGST</td><td className="text-right">{fmt(ts.folio_igst)}</td><td className="text-right">0.00</td><td className="text-right">{fmt(ts.total_igst)}</td></tr>
            <tr className="total-row"><td>TOTAL</td><td className="text-right"></td><td className="text-right"></td><td className="text-right">{fmt(ts.grand_total)}</td></tr>
          </tbody>
        </table>

        {/* Outstanding + Settlement side-by-side */}
        <div className="gm-flex-pair" style={{ marginTop: '0.5rem' }}>
          <div className="gm-flex-half">
            <h4 className="gm-section-title">Outstanding Summary</h4>
            <div className="gm-outstanding-text">
              Open Folios: <strong>{d.outstanding_summary?.open_folios?.count || 0}</strong> — ₹{fmt(d.outstanding_summary?.open_folios?.total_balance)}
            </div>
            {(d.outstanding_summary?.corporate?.length > 0) && (
              <table className="gm-table" style={{ marginBottom: '6px' }}>
                <thead><tr><th>Corporate</th><th className="text-right">Used</th><th className="text-right">Limit</th></tr></thead>
                <tbody>{d.outstanding_summary.corporate.map((r, i) => (
                  <tr key={i}><td>{r.name}</td><td className="text-right">{fmt(r.credit_used)}</td><td className="text-right">{fmt(r.credit_limit)}</td></tr>
                ))}</tbody>
              </table>
            )}
            {(d.outstanding_summary?.travel_agents?.length > 0) && (
              <table className="gm-table">
                <thead><tr><th>Travel Agent</th><th className="text-right">Used</th><th className="text-right">Limit</th></tr></thead>
                <tbody>{d.outstanding_summary.travel_agents.map((r, i) => (
                  <tr key={i}><td>{r.name}</td><td className="text-right">{fmt(r.credit_used)}</td><td className="text-right">{fmt(r.credit_limit)}</td></tr>
                ))}</tbody>
              </table>
            )}
          </div>
          <div className="gm-flex-half">
            <h4 className="gm-section-title">Settlement Analysis</h4>
            <table className="gm-table">
              <thead><tr><th>Mode</th><th className="text-right">Invoices</th><th className="text-right">Billed</th><th className="text-right">Collected</th><th className="text-right">Outstanding</th></tr></thead>
              <tbody>{(d.settlement_analysis || []).map((r, i) => (
                <tr key={i}><td>{r.mode}</td><td className="text-right">{r.invoice_count}</td><td className="text-right">{fmt(r.total_billed)}</td><td className="text-right">{fmt(r.total_collected)}</td><td className="text-right">{fmt(r.outstanding)}</td></tr>
              ))}</tbody>
            </table>
          </div>
        </div>

        {/* Daily Trend */}
        <h4 className="gm-section-title">Daily Trend</h4>
        <div className="gm-scroll-box">
          <table className="gm-table">
            <thead><tr>
              <th>Date</th><th className="text-right">Occupied</th><th className="text-right">Occ%</th><th className="text-right">ADR</th>
              <th className="text-right">Room Rev</th><th className="text-right">F&B Rev</th><th className="text-right">Total Rev</th><th className="text-right">Arrivals</th><th className="text-right">Departures</th>
            </tr></thead>
            <tbody>{(d.daily_trend || []).map((r, i) => (
              <tr key={i}>
                <td>{r.date}</td><td className="text-right">{r.rooms_occupied}</td><td className="text-right">{pct(r.occupancy_percent)}</td><td className="text-right">{fmt(r.adr)}</td>
                <td className="text-right">{fmt(r.room_revenue)}</td><td className="text-right">{fmt(r.fnb_revenue)}</td><td className="text-right">{fmt(r.total_revenue)}</td>
                <td className="text-right">{r.arrivals}</td><td className="text-right">{r.departures}</td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      </div>
    );
  };

  // ═══════════════════════════════════════════════════════════════════════
  // CSV Exports
  // ═══════════════════════════════════════════════════════════════════════
  const exportFlashCsv = () => {
    if (!flashData) return;
    // Bug 8689 — the CSV must mirror exactly what the Daily Flash dashboard
    // renders: per-room-type stats + TOTAL row, the F&B-outlet revenue lines
    // (previously omitted while the gross total still included them), and the
    // Tax / Payments KPI figures.
    const rs = flashData.room_statistics;
    const rv = flashData.revenue_summary;
    const k = flashData.kpis || {};
    const gm = flashData.guest_movement || {};
    const rows = [
      ['ROOM STATISTICS'],
      ['Room Type', 'Total', 'OOO', 'Sellable', 'Occupied', 'Available', 'Stayovers', 'Arrivals', 'Departures'],
      ...(rs.by_room_type || []).map(r => [r.room_type_name, r.total_rooms, r.ooo, r.sellable, r.occupied, r.available, r.stayovers, r.arrivals, r.departures]),
      ['TOTAL', rs.total_rooms, rs.out_of_order, rs.sellable, rs.occupied, rs.available, gm.stayovers, gm.arrivals, gm.departures],
      [''],
      ['KPIs'],
      ['Occupancy%', k.occupancy_percent], ['ADR', k.adr], ['RevPAR', k.revpar], ['TRevPAR', k.trevpar],
      ['Total Revenue', rv.total_gross_revenue], ['Total Tax', rv.total_tax], ['Total Payments', rv.total_payments],
      [''],
      ['REVENUE SUMMARY'],
      ['Category', 'Amount', 'CGST', 'SGST', 'IGST'],
      ...(rv.lines || []).map(r => [r.code, r.amount, r.cgst, r.sgst, r.igst]),
      ...(rv.fnb_by_outlet || []).map(r => [`F&B Outlet #${r.outlet_id}`, r.revenue, r.cgst, r.sgst, 0]),
      ['GROSS TOTAL', rv.total_gross_revenue, rv.cgst, rv.sgst, rv.igst],
    ];
    downloadCsv(`daily-flash-${flashDate}.csv`, ['Daily Flash Report', flashDate], rows);
  };

  const exportWeeklyCsv = () => {
    if (!weeklyData) return;
    // Bug 8690 — the CSV must mirror every section the Weekly dashboard shows,
    // not just the daily grid: weekly summary KPIs, segment, channel and room
    // type performance. Field names mirror renderWeekly exactly.
    const d = weeklyData;
    const s = d.weekly_summary || {};
    const rows = [
      ['WEEKLY SUMMARY'],
      ['Total Room Nights', s.total_room_nights], ['Days with Data', s.days_with_data],
      ['Avg Occupancy %', s.avg_occupancy], ['Avg ADR', s.avg_adr], ['Avg RevPAR', s.avg_revpar],
      ['Total Revenue', s.total_revenue], ['Total Tax', s.total_tax], ['Total Payments', s.total_payments],
      [''],
      ['DAILY PERFORMANCE GRID'],
      ['Date', 'Occupied', 'Occ%', 'ADR', 'RevPAR', 'Room Rev', 'F&B Rev', 'Total Rev', 'Arrivals', 'Departures'],
      ...(d.daily_grid || []).map(r => [r.date, r.rooms_occupied, r.occupancy_percent, r.adr, r.revpar, r.room_revenue, r.fnb_revenue, r.total_revenue, r.arrivals, r.departures]),
      [''],
      ['SEGMENT PERFORMANCE'],
      ['Segment', 'Reservations', 'Room Nights', 'Revenue'],
      ...(d.segment_performance || []).map(r => [r.segment, r.reservations, r.room_nights, r.revenue]),
      [''],
      ['CHANNEL PERFORMANCE'],
      ['Channel', 'Bookings', 'Room Nights', 'Revenue'],
      ...(d.channel_performance || []).map(r => [r.channel, r.bookings, r.room_nights, r.revenue]),
      [''],
      ['ROOM TYPE PERFORMANCE'],
      ['Room Type', 'Inventory', 'Sellable', 'Sold', 'Occupancy%'],
      ...(d.room_type_performance || []).map(r => [r.room_type_name, r.total_inventory_nights, r.sellable_nights, r.sold_nights, r.occupancy_percent]),
    ];
    downloadCsv(`weekly-${weekStart}.csv`, ['Weekly Performance Report', weekStart], rows);
  };

  const exportMonthlyCsv = () => {
    if (!monthlyData) return;
    // Bug 8691 — the CSV must mirror every section the Monthly dashboard shows,
    // not just the daily trend: financial summary, revenue by department & by
    // segment, tax summary, settlement analysis and outstanding summary.
    const d = monthlyData;
    const f = d.financial_summary || {};
    const ts = d.tax_summary || {};
    const os = d.outstanding_summary || {};
    const rows = [
      ['FINANCIAL SUMMARY'],
      ['Total Room Nights', f.total_room_nights], ['Days with Data', f.days_with_data],
      ['Avg Occupancy %', f.avg_occupancy], ['Avg ADR', f.avg_adr], ['Avg RevPAR', f.avg_revpar],
      ['Total Room Revenue', f.total_room_revenue], ['Total F&B Revenue', f.total_fnb_revenue],
      ['Total Revenue', f.total_revenue], ['Total Tax', f.total_tax],
      [''],
      ['REVENUE BY DEPARTMENT'],
      ['Department', 'Gross', 'CGST', 'SGST', 'IGST', 'Total'],
      ...(d.revenue_by_department || []).map(r => [r.department, r.gross_amount, r.cgst, r.sgst, r.igst,
        Number(r.gross_amount || 0) + Number(r.cgst || 0) + Number(r.sgst || 0) + Number(r.igst || 0)]),
      [''],
      ['REVENUE BY SEGMENT'],
      ['Segment', 'Reservations', 'Room Nights', 'Revenue'],
      ...(d.revenue_by_segment || []).map(r => [r.segment, r.reservations, r.room_nights, r.revenue]),
      [''],
      ['TAX SUMMARY'],
      ['Tax Type', 'Folio', 'F&B', 'Total'],
      ['CGST', ts.folio_cgst, ts.fnb_cgst, ts.total_cgst],
      ['SGST', ts.folio_sgst, ts.fnb_sgst, ts.total_sgst],
      ['IGST', ts.folio_igst, 0, ts.total_igst],
      ['TOTAL', '', '', ts.grand_total],
      [''],
      ['SETTLEMENT ANALYSIS'],
      ['Mode', 'Invoices', 'Billed', 'Collected', 'Outstanding'],
      ...(d.settlement_analysis || []).map(r => [r.mode, r.invoice_count, r.total_billed, r.total_collected, r.outstanding]),
      [''],
      ['OUTSTANDING SUMMARY'],
      ['Open Folios', os.open_folios?.count || 0, os.open_folios?.total_balance || 0],
      ...((os.corporate || []).length ? [['Corporate', 'Used', 'Limit'], ...os.corporate.map(r => [r.name, r.credit_used, r.credit_limit])] : []),
      ...((os.travel_agents || []).length ? [['Travel Agent', 'Used', 'Limit'], ...os.travel_agents.map(r => [r.name, r.credit_used, r.credit_limit])] : []),
      [''],
      ['DAILY TREND'],
      ['Date', 'Occupied', 'Occ%', 'ADR', 'Room Rev', 'F&B Rev', 'Total Rev', 'Arrivals', 'Departures'],
      ...(d.daily_trend || []).map(r => [r.date, r.rooms_occupied, r.occupancy_percent, r.adr, r.room_revenue, r.fnb_revenue, r.total_revenue, r.arrivals, r.departures]),
    ];
    downloadCsv(`monthly-${month}.csv`, ['Monthly Revenue Report', month], rows);
  };

  // ═══════════════════════════════════════════════════════════════════════
  // MAIN RENDER
  // ═══════════════════════════════════════════════════════════════════════
  return (
    <div>
      <h3>Front Office — GM Reports</h3>
      {error && <div className="error-banner">{error}</div>}

      {/* Tabs */}
      <div className="gm-tabs">
        <button className={`gm-tab${activeTab === 'daily-flash' ? ' active' : ''}`} onClick={() => setActiveTab('daily-flash')}>Daily Flash</button>
        <button className={`gm-tab${activeTab === 'weekly' ? ' active' : ''}`} onClick={() => setActiveTab('weekly')}>Weekly Performance</button>
        <button className={`gm-tab${activeTab === 'monthly' ? ' active' : ''}`} onClick={() => setActiveTab('monthly')}>Monthly Revenue</button>
      </div>

      {/* Controls */}
      <div className="filter-bar action-bar">
        {activeTab === 'daily-flash' && (
          <>
            <label className="form-label" style={{ marginBottom: 0 }}>Date:
              <input type="date" value={flashDate} onChange={e => setFlashDate(e.target.value)} style={{ marginLeft: '0.4rem' }} />
            </label>
            <button className="btn-primary" onClick={fetchFlash} disabled={loading}>{loading ? 'Loading...' : 'Generate'}</button>
            {flashData && <button className="btn-success" onClick={exportFlashCsv}>CSV</button>}
            {flashData && (
              <>
                <button className="btn-muted" onClick={() => downloadFlash('pdf')}>Print / PDF</button>
                <button className="btn-muted" onClick={() => downloadFlash('csv')}>CSV</button>
                <button className="btn-muted" onClick={() => downloadFlash('excel')}>Excel</button>
              </>
            )}
          </>
        )}
        {activeTab === 'weekly' && (
          <>
            <label className="form-label" style={{ marginBottom: 0 }}>Week Start:
              <input type="date" value={weekStart} onChange={e => setWeekStart(e.target.value)} style={{ marginLeft: '0.4rem' }} />
            </label>
            <button className="btn-primary" onClick={fetchWeekly} disabled={loading}>{loading ? 'Loading...' : 'Generate'}</button>
            {weeklyData && <button className="btn-success" onClick={exportWeeklyCsv}>CSV</button>}
            {weeklyData && <button className="btn-muted" onClick={downloadWeeklyPdf}>Print / PDF</button>}
          </>
        )}
        {activeTab === 'monthly' && (
          <>
            <label className="form-label" style={{ marginBottom: 0 }}>Month:
              <input type="month" value={month} onChange={e => setMonth(e.target.value)} style={{ marginLeft: '0.4rem' }} />
            </label>
            <button className="btn-primary" onClick={fetchMonthly} disabled={loading}>{loading ? 'Loading...' : 'Generate'}</button>
            {monthlyData && <button className="btn-success" onClick={exportMonthlyCsv}>CSV</button>}
            {monthlyData && <button className="btn-muted" onClick={downloadMonthlyPdf}>Print / PDF</button>}
          </>
        )}
      </div>

      {/* Report Content */}
      {loading && <div className="gm-loading">Loading report...</div>}
      {!loading && activeTab === 'daily-flash' && renderFlash()}
      {!loading && activeTab === 'weekly' && renderWeekly()}
      {!loading && activeTab === 'monthly' && renderMonthly()}
    </div>
  );
}

export default PmsGmReports;
